import _get from "lodash/get";
import { getInstitution, getProduct } from './common';
/**
 * Get the TEMPORARILY exposed, client derived details object.
 *
 * Only usable by clients who leverage `augmentMortgagesMarketplaceOfferWithClientDerivedDetails` utility on marketplace entities.
 * @see `../utils/mortgages/fetch.js`
 *
 * @param {Object} augmentedMarketplaceEntity
 * @returns {Object} - The client augmented details.
 */

var getClientDerivedDetails = function getClientDerivedDetails(augmentedMarketplaceEntity) {
  return _get(getProduct(augmentedMarketplaceEntity), '__client_derived_details');
};
/**
 * Get the featured loan attributes.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @returns {Array<String>} - The array of `MORTGAGES_FEATURED_LOAN_ATTRIBUTES`
 */


export var getFeaturedLoanAttributes = function getFeaturedLoanAttributes(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.featured_loan_attributes.value');
};
/**
 * Gets Apply Now link url.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The apply now url
 */

export var getApplyLink = function getApplyLink(marketplaceEntity) {
  var applyLink = _get(marketplaceEntity, 'derived_details.apply_link.value');

  return applyLink;
};
/**
 * Gets bottom line or "tagline" content.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The bottom line content
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value');
};
/**
 * Get the "cons" bullets.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @return {Array} - The "cons" array.
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value');
};
/**
 * Get the specified drivers to display on the page.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Array} - The drivers to display
 */

export var getDrivers = function getDrivers(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.drivers.value');
};
/**
 * Gets the alt text for the image associated with the lender
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The image "alt" text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value');
};
/**
 * Get the source URL of the image associated with the lender.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The url of the image
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.image_source.value');
};
/**
 * Get the monetization status of the lender offer.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Boolean} - The monetization status of lender product
 */

export var getIsMonetizing = function getIsMonetizing(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.is_monetizing.value');
};
/**
 * Get whether to enable rates experiences for the lender offer.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Boolean} - Whether to enable rates experiences for the lender offer.
 */

export var getIsRatesEnabled = function getIsRatesEnabled(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.is_rates_enabled.value');
};
/**
 * Get the issuer name.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The issuer's name
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value');
};
/**
 * Gets url to learn more page.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The "learn more" page url
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_link.value');
};
/**
 * Get the loan type.
 * @param {Object} marketplaceEntity - The marketplace entity
 * @return {String} - The loan type
 */

export var getLoanType = function getLoanType(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.loan_type.value');
};
/**
 * Get the max LTV (loan-to-value) percentage
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The max LTV
 */

export var getMaxLtvText = function getMaxLtvText(marketplaceEntity) {
  var maxLtvPercentage = _get(marketplaceEntity, 'details.max_ltv.value');

  return maxLtvPercentage;
};
/**
 * Get the min credit score.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min credit score
 */

export var getMinCreditScore = function getMinCreditScore(marketplaceEntity) {
  var creditScore = _get(marketplaceEntity, 'details.min_credit_score.value');

  return creditScore;
};
/**
 * Get the min credit score override that should display if within the tooltip.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min credit score tooltip override
 */

export var getMinCreditScoreTooltip = function getMinCreditScoreTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_credit_score_tooltip.value');
};
/**
 * Get the dictionary of min credit score information.
 * Uses temporarily added `__client_derived_details`.
 *
 * @param {Object} augmentedMarketplaceEntity - The Marketplace entity, augmented with `__client_derived_details`.
 * @return {Object} - The dictionary of min credit score information
 */

export var getMinCreditScoreDictionary = function getMinCreditScoreDictionary(augmentedMarketplaceEntity) {
  var clientDerivedMinCreditScores = _get(getClientDerivedDetails(augmentedMarketplaceEntity), 'min_credit_scores');

  return clientDerivedMinCreditScores;
};
/**
 * Get the list of credit score types to include in the tooltip.
 * Uses temporarily added `__client_derived_details`.
 *
 * @param {Object} augmentedMarketplaceEntity - The Marketplace entity, augmented with `__client_derived_details`.
 * @return {Array} - The list of credit score types to include in the tooltip
 */

export var getMinCreditScoreTypesForTooltip = function getMinCreditScoreTypesForTooltip(augmentedMarketplaceEntity) {
  var clientDerivedMinCreditTypes = _get(getClientDerivedDetails(augmentedMarketplaceEntity), 'featured_loan_types');

  return clientDerivedMinCreditTypes;
};
/**
 * Get the min down payment.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min down payment
 */

export var getMinDownPayment = function getMinDownPayment(marketplaceEntity) {
  var downPayment = _get(marketplaceEntity, 'details.min_down_payment.value');

  return downPayment;
};
/**
 * Get the min down payment tooltip text.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min down payment tooltip text
 */

export var getMinDownPaymentTooltip = function getMinDownPaymentTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_down_payment_tooltip.value');
};
/**
 * Get the mortech id.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The mortech id
 */

export var getMortechId = function getMortechId(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.mortech_id.value');
};
/**
 * Get the name to display for a lender.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The lender name
 */

export var getName = function getName(marketplaceEntity) {
  return getIssuerName(marketplaceEntity);
};
/**
 * Gets NMLS ID (Nationwide Mortgage Licensing System and Registry)
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @return {String} - The NMLS id.
 */

export var getNmlsId = function getNmlsId(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nmls_id.value');
};
/**
 * Get whether the mortgage lender is national/regional.
 *
 * @param   {Object} wpLenderObject   The WordPress lender entity
 * @return  {string}                  "National" or "Regional"
 */

export var getNationalOrRegional = function getNationalOrRegional(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.coverage_area.value');
};
/**
 * Get the "pros" bullets.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Array<String>} - The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value');
};
/**
 * Get the slug to identify the marketplace entity.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The slug
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'external_id');
};
/**
 * Get the star rating.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Number} - The star rating
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  var nwRating = _get(marketplaceEntity, 'details.rating_stars.value');

  return nwRating;
};
/**
 * Get the star rating tooltip.
 *
 * @return {string} - The star rating tooltip
 */

export var getStarRatingTooltip = function getStarRatingTooltip() {
  return "NerdWallet's ratings are determined by our editorial team. The scoring formula takes into account loan types and loan products offered, online conveniences, online mortgage rate information, and the rate spread and origination fee lenders reported in the latest available HMDA data.";
};